import type {
  Car,
  ChannelPreset,
  Driver,
  Event,
  ImportAssignment,
  Session,
  SessionSummary,
  Track,
  ViewState,
} from '@tla/types';

/** Channel descriptor returned by the import preview (no samples). */
export interface ChannelDescriptor {
  id: string;
  name: string;
  unit: string;
  group: string;
  source: string;
}

/** Preview returned by POST /api/import before the user confirms. */
export interface ImportPreview {
  importId: string;
  sourceFile: string;
  trackHint?: string;
  meta: Record<string, string>;
  channels: ChannelDescriptor[];
  lapCount: number;
  validLapCount: number;
  bestLapTime: number | null;
  firstTimestamp: number;
  durationSec: number;
  sampleCount: number;
}

export interface Share {
  id: string;
  viewState: ViewState;
  createdTs: number;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore non-JSON error bodies */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

/** Thin typed wrapper over the backend REST API. */
export const api = {
  // Tracks
  listTracks: () => fetch('/api/tracks').then(json<Track[]>),
  getTrack: (id: string) => fetch(`/api/tracks/${id}`).then(json<Track>),
  createTrack: (body: Partial<Track>) =>
    fetch('/api/tracks', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(json<Track>),
  updateTrack: (id: string, body: Track) =>
    fetch(`/api/tracks/${id}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(json<Track>),

  // Entities
  listDrivers: () => fetch('/api/drivers').then(json<Driver[]>),
  createDriver: (name: string) =>
    fetch('/api/drivers', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(json<Driver>),
  listCars: () => fetch('/api/cars').then(json<Car[]>),
  createCar: (name: string, description?: string) =>
    fetch('/api/cars', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, description }),
    }).then(json<Car>),
  listEvents: () => fetch('/api/events').then(json<Event[]>),
  createEvent: (name: string, date: string, trackId: string) =>
    fetch('/api/events', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, date, trackId }),
    }).then(json<Event>),

  // Sessions
  listSessions: (eventId?: string) =>
    fetch(`/api/sessions${eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''}`).then(
      json<Session[]>,
    ),
  getSession: (id: string) => fetch(`/api/sessions/${id}`).then(json<Session>),
  getSessionSummary: (id: string) =>
    fetch(`/api/sessions/${id}/summary`).then(json<SessionSummary>),
  getSessionData: async (id: string): Promise<Uint8Array> => {
    const res = await fetch(`/api/sessions/${id}/data`);
    if (!res.ok) throw new Error(`Failed to load telemetry: ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  },
  deleteSession: (id: string) =>
    fetch(`/api/sessions/${id}`, { method: 'DELETE' }).then((res) => {
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    }),

  // Import
  importFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return fetch('/api/import', { method: 'POST', body: form }).then(json<ImportPreview>);
  },
  confirmImport: (importId: string, assignment: ImportAssignment) =>
    fetch('/api/sessions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ importId, assignment }),
    }).then(json<Session>),

  // Presets
  listPresets: () => fetch('/api/presets').then(json<ChannelPreset[]>),
  createPreset: (preset: Omit<ChannelPreset, 'id'>) =>
    fetch('/api/presets', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(preset),
    }).then(json<ChannelPreset>),
  deletePreset: (id: string) =>
    fetch(`/api/presets/${id}`, { method: 'DELETE' }).then((res) => {
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    }),

  // Shares
  createShare: (viewState: ViewState) =>
    fetch('/api/shares', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ viewState }),
    }).then(json<Share>),
  getShare: (id: string) => fetch(`/api/shares/${id}`).then(json<Share>),
};
